import { useQueryClient } from '@tanstack/react-query';
import { Form, Input, Card, notification } from 'antd';
import Button from 'app/atoms/Button';
import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { changePassword } from './services';
import { IChangePasswordFormValues, IChangePasswordRequest } from './types';
import { PASSWORD_REGEX } from 'configs/regex';
import { fieldDefaultProps } from 'utils/fieldConfig';
import { NotificationProps } from 'utils/types';

const ChangePassword = (): ReactElement => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<IChangePasswordFormValues>();
  const [isLoading, setIsLoading] = useState(false);

  const onFinish = async (values: IChangePasswordFormValues) => {
    try {
      setIsLoading(true);
      const passwordData: IChangePasswordRequest = {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      };
      await changePassword(passwordData);
      notification.success({
        message: t('apiSuccessMessages.changePassword'),
        ...NotificationProps,
      });
      form.resetFields();
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as { response?: { data?: { message?: string } } };
        notification.error({
          message: apiError?.response?.data?.message || t('change-password.messages.error'),
          ...NotificationProps,
        });
      } else {
        notification.error({
          message: t('change-password.messages.error'),
          ...NotificationProps,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    queryClient.setQueryData(['header-props'], {
      selectedMenuItem: t('sideMenu.profile'),
      detailTitle: t('header.profile'),
      isLoading: false,
    });
  }, []);

  return (
    <div className="space-y-6">
      <Card>
        <p className="mb-6 text-neutral-700">{t('auth.signup.password_help')}</p>

        <Form form={form} layout="vertical" name="changePassword" onFinish={onFinish}>
          <Form.Item
            label={t('change-password.form.oldPassword')}
            name="currentPassword"
            className="label-before-content-none"
            validateTrigger="onBlur"
            rules={[{ required: true, message: t('change-password.form.required.oldPassword') }]}
          >
            <Input.Password
              {...fieldDefaultProps}
              placeholder={t('change-password.form.placeholders.oldPassword')}
              data-testid="old-password-field"
            />
          </Form.Item>

          <Form.Item
            label={t('change-password.form.newPassword')}
            name="newPassword"
            className="label-before-content-none"
            dependencies={['currentPassword']}
            validateTrigger="onBlur"
            rules={[
              { required: true, message: t('change-password.form.required.newPassword') },
              {
                pattern: PASSWORD_REGEX,
                min: 8,
                max: 16,
                message: t('auth.signup.rules.password_invalid'),
                validateTrigger: 'onBlur',
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('currentPassword') !== value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('change-password.form.required.samePassword')));
                },
                validateTrigger: 'onBlur',
              }),
            ]}
          >
            <Input.Password
              {...fieldDefaultProps}
              placeholder={t('change-password.form.placeholders.newPassword')}
              data-testid="new-password-field"
              maxLength={16}
            />
          </Form.Item>

          <Form.Item
            label={t('change-password.form.confirmPassword')}
            name="confirmPassword"
            className="label-before-content-none"
            dependencies={['newPassword']}
            validateTrigger="onBlur"
            rules={[
              { required: true, message: t('change-password.form.required.confirmPassword') },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('change-password.form.required.passwordMismatch')));
                },
                validateTrigger: 'onBlur',
              }),
            ]}
          >
            <Input.Password
              {...fieldDefaultProps}
              placeholder={t('change-password.form.placeholders.confirmPassword')}
              data-testid="confirm-password-field"
            />
          </Form.Item>

          <div className="flex justify-end">
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={isLoading}
                data-testid="update-password-button"
              >
                {t('change-password.form.updatePassword')}
              </Button>
            </Form.Item>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default ChangePassword;
